import { z } from 'zod';
import type {
  EFaxCommunicationPartT,
  OutboundCommunicationT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { PatientReferral } from './fetchPatientReferrals';
import { getLotus } from '@/shared/api/lotus';

const outboundCommunicationResponseSchema = z.array(
  z.object({
    id: z.string(),
    patient_id: z.string(),
    subject: z.string(),
    body: z.string().nullable(),
    attachments: z.array(z.unknown()),
    method_specific_part: z
      .object({
        type: z.literal('metriport'),
        message_id: z.string(),
        destination_oid: z.string().nullable().optional(),
        sender: z.string().nullable().optional(),
      })
      .or(
        z.object({
          type: z.literal('efax'),
          fax_id: z.string(),
          remote_fax_number: z.string(),
        })
      ),
    error_details: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    direction: z.literal('outbound'),
    status: z.enum(['in_progress', 'delivered', 'failed']),
    delivered_at: z.string().nullable(),
  })
);

export async function fetchPatientOutboundReferrals(
  userId: string,
  signal?: AbortSignal
): Promise<PatientReferral[]> {
  const response = await getLotus<unknown>(`/greenfield/referrals/${userId}/outbound`, {
    signal,
  });
  const referrals = outboundCommunicationResponseSchema.parse(
    response.data
  ) as OutboundCommunicationT[];

  return referrals.map(
    (referral): PatientReferral => ({
      id: referral.id,
      createdAt: referral.created_at,
      updatedAt: referral.updated_at,
      communication: {
        direction: 'outbound',
        id: referral.id,
        patientId: referral.patient_id,
        subject: referral.subject,
        body: referral.body,
        attachments: referral.attachments,
        methodSpecificPart:
          referral.method_specific_part.type === 'metriport'
            ? {
                type: 'metriport',
                messageId: referral.method_specific_part.message_id,
                sender: referral.method_specific_part.sender ?? null,
              }
            : {
                type: 'efax',
                faxId: (referral.method_specific_part as EFaxCommunicationPartT).fax_id,
                remoteFaxNumber: (referral.method_specific_part as EFaxCommunicationPartT)
                  .remote_fax_number,
              },
        status: referral.status,
        receivedAt: null,
        downloadedAt: null,
        createdAt: referral.created_at,
        updatedAt: referral.updated_at,
        errorDetails: referral.error_details,
      },
    })
  );
}
